/**
 * Founder Control Center v2 · Sección 2 · Intelligence.
 *
 * Orden V2 spec §3.1 (`FOUNDER_CONTROL_CENTER_V2.md`):
 *   1. Insights        → SuperAdmin tab "ai-insights" (embed)
 *   2. Auditorías      → AuditCenter (FO)              [Audit Center]
 *   3. Knowledge Base  → KnowledgeBase (FO · regex)
 *   4. KB Manager      → KnowledgeBaseManager (FO · RAG · CRUD Mongo)
 *   5. Funnel          → SuperAdmin tab "funnel" (embed)
 *   6. Alertas         → SuperAdmin tab "churn" (embed) [Churn Risk + NPS deltas]
 *   7. Founder Copilot → Copilot (AG-07)
 *   8. Diagnostics     → Diagnostics (FO · health + diagnostics consolidados)
 *
 * Fase 6 Sprint Q (Feb 2026):
 *   ✅ MOVIDO · Event Log eliminado de Intelligence y trasladado a Security
 *     (V2 spec §7) en /founder/security?sub=event-log. Deep-link legacy
 *     `?sub=event-log` se redirige automáticamente al destino correcto.
 *
 * 0 endpoints backend tocados. Solo reorganización de UX.
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Lightning, FileText, Books, ChartLineUp, Bell, Robot, Database, Heartbeat,
  TreeStructure,
} from "@phosphor-icons/react";
import SuperAdmin from "../../SuperAdmin";
import AuditCenter from "../AuditCenter";
import KnowledgeBase from "../KnowledgeBase";
import KnowledgeBaseManager from "../KnowledgeBaseManager";
import Copilot from "../Copilot";
import Diagnostics from "../Diagnostics";
import TaxonomyExplorer from "../TaxonomyExplorer";
import SubTabs, { useSubTab } from "./SubTabs";

const ITEMS = [
  { key: "insights",          label: "Insights",         icon: Lightning },
  { key: "auditorias",        label: "Audit Center",     icon: FileText },
  { key: "knowledge",         label: "Knowledge Base",   icon: Books },
  { key: "kb-manager",        label: "KB Manager · RAG", icon: Database },
  { key: "taxonomy-explorer", label: "Taxonomy Explorer", icon: TreeStructure },
  { key: "funnel",            label: "Funnel",           icon: ChartLineUp },
  { key: "alertas",           label: "Alertas",          icon: Bell },
  { key: "copilot",           label: "AG-07 Copilot",    icon: Robot },
  { key: "diagnostics",       label: "Diagnostics",      icon: Heartbeat },
];

export default function IntelligenceSection() {
  const [sub, setSub] = useSubTab("insights");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Legacy alias: Event Log se movió a Security en Fase 6 (V2 §7).
  useEffect(() => {
    if (searchParams.get("sub") === "event-log") {
      navigate("/founder/security?sub=event-log", { replace: true });
    }
  }, [searchParams, navigate]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-5" data-testid="fcc-intelligence-section">
      <Header />
      <SubTabs items={ITEMS} value={sub} onChange={setSub} testid="intelligence-subtabs" />
      <div className="pt-2">
        {sub === "insights"          && <SuperAdmin embedded forceTab="ai-insights" />}
        {sub === "auditorias"        && <AuditCenter />}
        {sub === "knowledge"         && <KnowledgeBase />}
        {sub === "kb-manager"        && <KnowledgeBaseManager />}
        {sub === "taxonomy-explorer" && <TaxonomyExplorer />}
        {sub === "funnel"            && <SuperAdmin embedded forceTab="funnel" />}
        {sub === "alertas"           && <SuperAdmin embedded forceTab="churn" />}
        {sub === "copilot"           && <Copilot />}
        {sub === "diagnostics"       && <Diagnostics />}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
        <Lightning size={11} weight="fill" /> Founder Control Center · Intelligence
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
        Analiza el negocio
      </h1>
      <p className="text-xs text-[var(--ag-text-dim)] mt-1">
        Insights · Audit Center · Knowledge Base · KB Manager (RAG) · Taxonomy Explorer · Funnel · Alertas · AG-07 Copilot · Diagnostics
      </p>
    </div>
  );
}
